// Хийх зүйлийн хуудас (эцэг эх) — /todo/:id. Төрлөөр нь:
//   letter: захидал бичих талбар, үг тоолуур, багадаа minWords үг бол "Илгээх"; илгээснийг дараа нь засна
//   consent: зөвшөөрлийн текст + нэрээ бичиж баталгаажуулна
//   read: гарын авлагыг нээгээд "Уншсан" гэж баталгаажуулна
// Баталгаажуулсны дараа нүүрний "Таны хийх зүйлс"-д ✓ болно.
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Spin } from "antd";
import { FiArrowLeft, FiCheck, FiClock, FiExternalLink } from "react-icons/fi";
import dayjs from "dayjs";
import { instance } from "utils/axios";
import { dueText } from "utils/due";

const words = (t) => String(t || "").trim().split(/\s+/).filter(Boolean).length;
const TAG = { letter: "Захидал", consent: "Зөвшөөрөл", read: "Унших" };

function DoneNote({ t }) {
  if (!t.done) return null;
  const edited = t.updatedAt && t.doneAt && dayjs(t.updatedAt).diff(dayjs(t.doneAt), "minute") > 0;
  return (
    <div className="flex items-center gap-2.5 rounded-[14px] bg-[#dcfce7] text-[#15803d] px-4 py-3 text-sm font-semibold">
      <span className="h-6 w-6 rounded-full bg-[#22c55e] text-white flex items-center justify-center shrink-0"><FiCheck size={14} strokeWidth={3} /></span>
      <span>
        {t.action === "letter" ? "Илгээсэн" : "Баталгаажуулсан"} · {dayjs(t.doneAt).format("MM/DD HH:mm")}
        {edited ? ` · засварласан ${dayjs(t.updatedAt).format("MM/DD HH:mm")}` : ""}
      </span>
    </div>
  );
}

function Letter({ t, onSaved }) {
  const [text, setText] = useState(t.response || "");
  const [editing, setEditing] = useState(!t.done);
  const [saving, setSaving] = useState(false);
  const n = words(text);
  const min = t.minWords || 0;
  const ok = n >= min;
  const save = () => {
    setSaving(true);
    instance({ method: "post", url: `/guardian/todos/${t.id}/done`, data: { response: text } })
      .then((res) => { onSaved(res.data); setEditing(false); })
      .catch(() => {})
      .then(() => setSaving(false));
  };

  if (!editing) {
    return (
      <div className="flex flex-col gap-3">
        <div className="rounded-[14px] border border-[#e9e8f0] bg-white p-4 md:p-5 text-[15px] leading-relaxed text-[#1c1c1c] whitespace-pre-line">{t.response}</div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-[#6e5e8a]">{words(t.response)} үг</span>
          <button onClick={() => setEditing(true)} className="h-11 px-5 rounded-full border border-[#e5d8f5] bg-white text-primary text-[15px] font-bold hover:bg-[#faf7ff]">Засах</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} autoFocus={!t.done}
        placeholder="Хайрт хүүхэддээ…"
        className="w-full rounded-[14px] border border-[#e5d8f5] bg-white p-4 text-[16px] leading-relaxed text-[#1c1c1c] outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 resize-y" />
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-28 h-1.5 rounded-full bg-[#eef0f4] overflow-hidden">
            <div className={`h-full rounded-full ${ok ? "bg-[#22c55e]" : "bg-primary"}`} style={{ width: `${Math.min(100, min ? (n * 100) / min : 100)}%` }} />
          </div>
          <span className={`text-sm font-semibold ${ok ? "text-[#15803d]" : "text-[#6e5e8a]"}`}>{n} / {min} үг</span>
        </div>
        <div className="flex gap-2">
          {t.done && <button onClick={() => { setText(t.response || ""); setEditing(false); }} className="h-11 px-5 rounded-full border border-[#e5d8f5] bg-white text-[#5b4e70] text-[15px] font-bold">Болих</button>}
          <button onClick={save} disabled={!ok || saving}
            className="flex-1 sm:flex-none h-11 px-6 rounded-full bg-primary text-white text-[15px] font-bold hover:bg-[#4b0d76] disabled:bg-[#d9cdea] disabled:cursor-not-allowed">
            {saving ? "Илгээж байна…" : t.done ? "Хадгалах" : "Илгээх"}
          </button>
        </div>
      </div>
      {!ok && <span className="text-[13px] text-[#6e5e8a]">Илгээхэд дахиад {min - n} үг дутуу байна.</span>}
    </div>
  );
}

function Confirm({ t, onSaved }) {
  const [name, setName] = useState(t.response || "");
  const [saving, setSaving] = useState(false);
  const consent = t.action === "consent";
  const save = () => {
    setSaving(true);
    instance({ method: "post", url: `/guardian/todos/${t.id}/done`, data: { response: consent ? name : undefined } })
      .then((res) => onSaved(res.data))
      .catch(() => {})
      .then(() => setSaving(false));
  };
  return (
    <div className="flex flex-col gap-3">
      {t.linkUrl && (
        <a href={t.linkUrl} target="_blank" rel="noopener noreferrer"
          className="self-start h-11 px-5 rounded-full border border-[#e5d8f5] bg-white text-primary text-[15px] font-bold inline-flex items-center gap-2 hover:bg-[#faf7ff] hover:text-primary">
          {t.linkLabel || (consent ? "Зөвшөөрлийн хуудас" : "Гарын авлага нээх")}<FiExternalLink size={16} />
        </a>
      )}
      {consent && (
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-[#1c1c1c]">Эцэг эх / асран хамгаалагчийн нэр</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Овог нэр"
            className="h-12 rounded-xl border border-[#e5d8f5] bg-white px-4 text-[16px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
        </label>
      )}
      {!t.done && (
        <button onClick={save} disabled={saving || (consent && !name.trim())}
          className="h-11 px-6 rounded-full bg-primary text-white text-[15px] font-bold hover:bg-[#4b0d76] disabled:bg-[#d9cdea] disabled:cursor-not-allowed sm:self-start">
          {consent ? "Зөвшөөрч байна" : "Уншсан"}
        </button>
      )}
    </div>
  );
}

export default function TodoPage() {
  const { id } = useParams();
  const [t, setT] = useState(undefined);
  useEffect(() => {
    instance({ method: "get", url: `/guardian/todos/${id}` })
      .then((res) => setT(res.status === 204 ? null : res.data))
      .catch(() => setT(null));
  }, [id]);

  return (
    <div className="pt-3.5 md:pt-7 pb-8 max-w-[760px] mx-auto flex flex-col gap-4">
      <Link to="/" className="self-start inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-[#3d1b6e] min-h-[44px]">
        <FiArrowLeft size={16} /> Нүүр
      </Link>
      {t === undefined ? (
        <div className="flex justify-center py-10"><Spin /></div>
      ) : !t ? (
        <section className="tj-card p-6 text-center text-sm text-[#6e5e8a]">Энэ хийх зүйл олдсонгүй эсвэл хаагдсан байна.</section>
      ) : (
        <section className="tj-card p-4 md:p-7 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] md:text-xs font-bold tracking-[0.12em] uppercase text-[#0e7490]">{TAG[t.action] || "Хийх зүйл"}</span>
            <h1 className="m-0 text-[22px] md:text-[28px] font-extrabold leading-snug tracking-[-0.01em] text-[#1c1c1c]">{t.title}</h1>
            {t.dueDate && <span className="inline-flex items-center gap-1.5 text-sm text-[#6e5e8a]"><FiClock size={14} />{dueText(t.dueDate)}</span>}
          </div>
          {t.body && <p className="m-0 text-[15px] leading-relaxed text-[#5b4e70] whitespace-pre-line">{t.body.replace(/^\[ТЕСТ\d*\]\s*/, "")}</p>}
          <DoneNote t={t} />
          {t.action === "letter" ? <Letter t={t} onSaved={setT} /> : <Confirm t={t} onSaved={setT} />}
        </section>
      )}
    </div>
  );
}
